import React, {useEffect, useRef, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import get from 'lodash/get';
import {PopoverBody} from 'reactstrap';
import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {OverlayProps, PopoverForm, PopoverHeading} from 'app/general/overlays';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import {StyledBulkActionDiv} from 'app/general/widgets/SimpleItemPickerList/SimpleItemPickerList';
import StateButton from 'app/general/widgets/StateButton';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {BasicUserSerializedType, ContentType} from 'sweet-types';

const StyledManagersUl = styled.ul.attrs({
  className: 'list-unstyled mb-1',
})`
  max-height: 50vh;
  overflow: auto;
  border-top: 1px solid ${colors.lightGray};
  border-bottom: 1px solid ${colors.lightGray};

  li {
    border-bottom: 1px solid ${colors.lightGray};
  }

  .form-check-input {
    margin-top: 0;
  }
`;

type Props = {
  managers: Array<BasicUserSerializedType>;
  contentType: ContentType;
  onSubmit: (arg0: string, arg1: Array<string>) => Promise<any>;
  overlayProps: OverlayProps;
  refreshData?: () => unknown;
};

export const RequestApprovalPopover = React.forwardRef<HTMLDivElement, Props>(
  (props: Props, forwardedRef) => {
    const {contentType, managers, overlayProps, onSubmit, refreshData} = props;
    const [selected, setSelected] = useState<Array<string>>([]);
    const [filtered, setFiltered] =
      useState<Array<BasicUserSerializedType>>(managers);
    const [message, setMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
      undefined
    );

    const errorClass = errorMessage != null ? 'is-invalid' : '';
    const doctypeName = getContentType(contentType).name;
    const filteredURLs = filtered.map((m) => m.url);
    const allSelected =
      selected.filter((url) => filteredURLs.includes(url)).length ===
      filtered.length;

    const bulkCheckbox = useRef<HTMLInputElement>(null);

    function onSearchChange(s: string) {
      const upperSearch = s.toUpperCase();
      setFiltered(
        managers.filter(
          (manager) =>
            manager.name.toUpperCase().includes(upperSearch) ||
            manager.email.toUpperCase().includes(upperSearch)
        )
      );
    }

    useEffect(() => {
      if (overlayProps.isOpen && refreshData != null) {
        refreshData();
      }
    }, [overlayProps.isOpen]);

    function toggle(managerUrl: string) {
      setSelected(
        selected.indexOf(managerUrl) === -1
          ? selected.concat([managerUrl])
          : selected.filter((id) => id !== managerUrl)
      );
    }

    function toggleSelectAll() {
      // handles case where someone searches (filtered) and selects all
      // or deselects all. It should just operate on those filtered items
      // and leave the other unfiltered items alone.
      const filteredURLs = filtered.map((m) => m.url);
      const base = selected.filter((url) => !filteredURLs.includes(url));
      setSelected(allSelected ? base : base.concat(filteredURLs));
    }

    function updateCheckboxState(
      filtered: Array<string>,
      selected: Array<string>
    ) {
      const checkboxEl: HTMLInputElement | null = bulkCheckbox.current;

      if (checkboxEl != null) {
        const selectedLen = selected.filter((url) =>
          filtered.includes(url)
        ).length;
        checkboxEl.indeterminate =
          selectedLen < filtered.length && selectedLen > 0;
      }
    }

    function requestApproval() {
      return onSubmit(message, selected).catch((err) => {
        setErrorMessage(
          get(err, 'data.message[0]') || gt('Could not request approval.')
        );
        throw err;
      });
    }

    function handleInputChange(event) {
      const target = event.target;
      setMessage(target.value);
    }
    updateCheckboxState(
      filtered.map((m) => m.url),
      selected
    );

    return (
      <PopoverForm overlayProps={overlayProps}>
        {({close, closeOnSuccess}) => (
          <div ref={forwardedRef}>
            <PopoverHeading>{gt('Request approval')}</PopoverHeading>
            <PopoverBody>
              <p>
                {gt(
                  'The request will be sent to these selected %(doctypeName)s managers:',
                  {
                    doctypeName,
                  }
                )}
              </p>
              <div className="actions-block pb-1 pr-1">
                <StyledBulkActionDiv className="d-flex justify-content-between">
                  <label className="checkbox mb-0">
                    <input
                      type="checkbox"
                      ref={bulkCheckbox}
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />{' '}
                    {allSelected ? gt('Unselect all') : gt('Select all')}
                  </label>
                  <div className="text-muted">{selected.length}</div>
                </StyledBulkActionDiv>
                <div className="mt-2">
                  <SearchInputField
                    autoFocus={true}
                    placeholder={gt('Search Managers')}
                    onChange={onSearchChange}
                  />
                </div>
              </div>
              <StyledManagersUl>
                {filtered.map((manager) => (
                  <li key={manager.url}>
                    <label className="d-flex align-items-center my-1 form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selected.indexOf(manager.url) > -1}
                        onChange={() => toggle(manager.url)}
                      />
                      <div className="avatar-container">
                        <UserAvatar
                          small={true}
                          user={manager}
                          className="d-block"
                        />
                      </div>
                      <div className="ml-2">{manager.name}</div>
                    </label>
                  </li>
                ))}
              </StyledManagersUl>
              <p>{gt('When approved this draft will be live')}</p>
              <div className="is-invalid">
                <textarea
                  name="message"
                  className={`form-control form-control-sm ${errorClass}`}
                  value={message}
                  onChange={handleInputChange}
                  placeholder={gt('Describe your changes')}
                />
                {errorMessage != null ? (
                  <div className="invalid-feedback">{errorMessage}</div>
                ) : null}
              </div>
            </PopoverBody>

            <StyledPopoverBottomDIV>
              <button
                className="btn btn-link btn-sm"
                onClick={() => {
                  setFiltered(managers);
                  setSelected([]);
                  close();
                }}
              >
                <i className="fa fa-times" /> {gt('Cancel')}
              </button>
              <StateButton
                disabled={selected.length === 0}
                onClick={() => closeOnSuccess(requestApproval())}
                className="btn-primary btn-sm"
                busyText={gt('Submit')}
              >
                {gt('Submit')}
              </StateButton>
            </StyledPopoverBottomDIV>
          </div>
        )}
      </PopoverForm>
    );
  }
);
